// components/QuestionnaireBienModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ClipboardIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
const fmtBool = (b) => (b === true ? "Oui" : b === false ? "Non" : "—");

// Labels (comme ton form)
const USAGES_LABELS = {
  habitation: "Habitation",
  professionnel: "Professionnel",
  mixte: "Mixte habitation-professionnel",
  investissementLocatif: "Investissement locatif",
};

const LOUE_LABELS = {
  oui: "Oui",
  non: "Non",
};

const ETATS_LABELS = {
  aucun_travaux: "Aucun travaux à prévoir",
  a_rafraichir: "À rafraîchir",
  a_renover: "À rénover",
};

const ANNEXES_LABELS = {
  balcon: "Balcon",
  loggia: "Loggia",
  cellier: "Cellier",
  grenier: "Grenier",
  jardinCours: "Jardin / cour",
  piscine: "Piscine",
  terrasse: "Terrasse",
  garage: "Garage",
  parking: "Parking",
  cave: "Cave",
};

function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toCsv(rows) {
  const esc = (s) => {
    const v = String(s ?? "");
    return /[;\n"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const lines = [
    ["Intitulé", "Réponse"].join(";"),
    ...rows.map(({ label, value }) => [esc(label), esc(value)].join(";")),
  ];
  return lines.join("\n");
}

// Section UI
const Section = ({ title, items, onCopy, copiedKey }) => {
  if (!items || items.length === 0) return null;

  return (
    <section className="mb-6">
      <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
      <div className="overflow-hidden rounded-xl ring-1 ring-black/5 bg-white">
        <dl className="divide-y divide-gray-200">
          {items.map(({ key, label, value }) => (
            <div key={key} className="grid grid-cols-1 sm:grid-cols-12 items-start gap-3 p-4 sm:p-5">
              <dt className="sm:col-span-4 text-sm font-medium text-gray-700">{label}</dt>
              <dd className="sm:col-span-7 text-sm text-gray-900 whitespace-pre-wrap break-words">
                {hasValue(value) ? String(value) : "—"}
              </dd>
              <div className="sm:col-span-1 flex sm:justify-end">
                <button
                  type="button"
                  onClick={() => onCopy(key, String(value ?? ""))}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  title="Copier"
                >
                  {copiedKey === key ? (
                    <>
                      <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600" />
                      Copié
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="h-4 w-4 text-gray-500" />
                      Copier
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

//https://cloterenotaire.onrender.com
//http://localhost:5050
export default function QuestionnaireBienModal({
  open,
  onClose,
  id_action,
  apiBase = API_BASE,
  title = "Questionnaire bien",
}) {
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null); // ici row = payload
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  // Fetch
  useEffect(() => {
    if (!open || !id_action) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await axios.get(
          `${apiBase}/api/questionnaireBien/${encodeURIComponent(id_action)}`
        );

        if (cancelled) return;

        if (data?.ok) {
          setRow(data?.payload ?? null);
        } else {
          setRow(null);
          setError(data?.message ?? "Aucune donnée");
        }
      } catch (e) {
        console.error("❌ fetch questionnaire bien:", e);
        if (!cancelled) setError("Impossible de charger le questionnaire.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, id_action, apiBase]);

  const copyToClipboard = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value ?? "");
      setCopied(field);
      setTimeout(() => setCopied(null), 1500);
    } catch (err) {
      console.error("copy error", err);
    }
  };

  // Prépare les sections à partir de row.bien
  const sections = useMemo(() => {
    const b = row?.bien;
    if (!b) return null;

    const sectionAdresse = [
      { key: "bien_adresse", label: "Adresse complète du bien", value: b?.adresse },
    ].filter((f) => hasValue(f.value));

    const sectionTypeUsage = [
      { key: "bien_type", label: "Type de bien", value: b?.type },
      { key: "bien_usage", label: "Usage du bien", value: USAGES_LABELS[b?.usage] ?? b?.usage },
      { key: "bien_loue", label: "Le bien est-il loué ?", value: LOUE_LABELS[b?.loue] ?? b?.loue },
    ].filter((f) => hasValue(f.value));

    const d = b?.description || {};
    const sectionDescription = [
      { key: "desc_surfaceHabitable", label: "Surface habitable (m²)", value: d?.surfaceHabitable },
      { key: "desc_nbChambresService", label: "Nb de chambres", value: d?.nbChambresService },
      { key: "desc_nbPiecesPrincipales", label: "Nb de pièces principales", value: d?.nbPiecesPrincipales },
      { key: "desc_nbWC", label: "Nb de WC", value: d?.nbWC },
      { key: "desc_nbSDB", label: "Nb de SDB", value: d?.nbSDB },
    ].filter((f) => hasValue(f.value));

    const s = b?.situation || {};
    const sectionSituation = [
      { key: "sit_batiment", label: "Bâtiment", value: s?.batiment },
      { key: "sit_escalier", label: "Escalier", value: s?.escalier },
      { key: "sit_etage", label: "Étage", value: s?.etage },
      { key: "sit_ascenseur", label: "Ascenseur", value: fmtBool(s?.ascenseur) },
      { key: "sit_chauffage", label: "Chauffage", value: s?.chauffage },
    ].filter((f) => hasValue(f.value) && f.value !== "—");

    const a = b?.annexes || {};
    const annexeItems = Object.keys(ANNEXES_LABELS)
      .map((k) => {
        const count = Number(a?.[k] ?? 0);
        if (!count || count <= 0) return null;
        return { key: `annexe_${k}`, label: ANNEXES_LABELS[k], value: String(count) };
      })
      .filter(Boolean);

    const sectionEtat = [
      { key: "bien_etat", label: "État du bien", value: ETATS_LABELS[b?.etat] ?? b?.etat },
    ].filter((f) => hasValue(f.value));

    return {
      adresse: sectionAdresse,
      typeUsage: sectionTypeUsage,
      description: sectionDescription,
      situation: sectionSituation,
      annexes: annexeItems,
      etat: sectionEtat,
    };
  }, [row]);

  const getVisibleItemsFlat = () => {
    if (!sections) return [];
    const flat = [];
    const push = (arr) => arr?.forEach((it) => flat.push({ label: it.label, value: it.value }));
    push(sections.adresse);
    push(sections.typeUsage);
    push(sections.description);
    push(sections.situation);
    push(sections.annexes);
    push(sections.etat);
    return flat;
  };

  const handleExportCsv = () => {
    const items = getVisibleItemsFlat();
    const csv = toCsv(items);
    const suffix = id_action ? `_${id_action}` : "";
    const now = new Date();
    const iso = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
    ].join("");
    const filename = `questionnaire_bien${suffix}_${iso}.csv`;
    downloadBlob(csv, "text/csv;charset=utf-8", filename);
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
      <div className="fixed inset-0 p-4 flex items-center justify-center">
        <DialogPanel className="pointer-events-auto w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {title}
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={loading || !row}
                className="btn-primary"
                title="Exporter en CSV"
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
                title="Fermer"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            {loading && (
              <div className="rounded-md bg-slate-50 p-4 mb-3">
                <p className="text-sm text-slate-700 m-0">Chargement…</p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-3">
                <p className="text-sm text-red-700 m-0">{error}</p>
              </div>
            )}

            {!loading && !error && !row && (
              <div className="rounded-md bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 m-0">Aucun questionnaire trouvé.</p>
              </div>
            )}

            {!loading && !error && row && (
              <>
                <Section
                  title="Adresse du bien"
                  items={sections?.adresse}
                  onCopy={copyToClipboard}
                  copiedKey={copied}
                />
                <Section
                  title="Type & usage"
                  items={sections?.typeUsage}
                  onCopy={copyToClipboard}
                  copiedKey={copied}
                />
                <Section
                  title="Description & composition"
                  items={sections?.description}
                  onCopy={copyToClipboard}
                  copiedKey={copied}
                />
                <Section
                  title="Situation"
                  items={sections?.situation}
                  onCopy={copyToClipboard}
                  copiedKey={copied}
                />
                <Section
                  title="Annexes / Dépendances"
                  items={sections?.annexes}
                  onCopy={copyToClipboard}
                  copiedKey={copied}
                />
                <Section
                  title="État du bien"
                  items={sections?.etat}
                  onCopy={copyToClipboard}
                  copiedKey={copied}
                />
              </>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}